/**
 * UniversalGrid — TanStack Table 驱动的通用数据表格
 *
 * Features:
 *   - Schema → TanStack columnDefs mapper (复用 ListColumnDSL)
 *   - 虚拟滚动 (大数据集)
 *   - 列宽拖拽调整
 *   - 列固定 (pinned left/right)
 *   - 行内编辑 (双击单元格)
 *   - 锁定指示 (🔒)
 *   - 行选择 + 批量操作
 */

import { useState, useCallback, useMemo, useRef, type ReactNode } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
  type ColumnSizingState,
  type RowSelectionState,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useTranslation } from 'react-i18next';
import type { EntityMeta, FieldDef } from '@/types/entity';
import type { ListColumnDSL, ThreeLineRowDSL, ThreeLineElement, ColumnGroupDSL } from '@/lib/use-view-template';
import { formatCell, summarizeValue } from '@/lib/format';
import { DENSITY_CLASSES, type RowDensity } from '@/hooks/useGridSettings';
import { GridCellEditor, LockIndicator, getCellEditability } from '@/components/grid/GridCellEditor';
import { Badge } from '@/components/ui/badge';
import { ArrowUp, ArrowDown, ArrowUpDown, Check, Copy, Pencil } from 'lucide-react';

// ─── Types ─────────────────────────────────────────

type DataRow = Record<string, unknown>;

export interface RefResolverHandle {
  getDisplay: (field: string, id: string) => string | null | undefined;
  isFkField: (field: string) => boolean;
}

export type RowMode = '1-line' | '3-line' | 'basic' | 'json';

export interface UniversalGridProps {
  slug: string;
  meta: EntityMeta;
  columns: ListColumnDSL[];
  data: DataRow[];
  total: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onSort: (field: string, order: 'asc' | 'desc') => void;
  onCellEdit?: (rowId: string, field: string, value: unknown) => Promise<void>;
  onRowClick?: (row: DataRow) => void;
  onSelectionChange?: (selectedIds: string[]) => void;
  userRole?: string;
  loading?: boolean;
  sortField?: string;
  sortOrder?: 'asc' | 'desc';
  refResolver?: RefResolverHandle;
  density?: RowDensity;
  onQuickEdit?: (row: DataRow) => void;
  onQuickCopyRow?: (row: DataRow) => void;
  rowClassName?: (row: DataRow) => string | undefined;
  /** Raw TanStack ColumnDef columns prepended after the selection checkbox. */
  prependColumns?: ColumnDef<DataRow>[];
  /** Row display mode: 1-line (default spreadsheet) or 3-line (compact card-like rows) */
  rowMode?: RowMode;
  /** 3-line mode: row-level layout config — which fields appear on each of the 3 lines */
  threeLineConfig?: ThreeLineRowDSL;
  /** Column grouping — segments columns into labeled groups (affects both 1-line headers and 3-line card blocks) */
  columnGroups?: ColumnGroupDSL[];
}

// ─── Helpers ───────────────────────────────────────

function getFieldDef(meta: EntityMeta, fieldName: string): FieldDef | undefined {
  return meta.fields.find((f) => f.name === fieldName);
}

function getRowId(row: DataRow): string {
  return String(row.id ?? row.uuid ?? '');
}

function getRowValue(row: DataRow, fieldName: string, meta: EntityMeta): unknown {
  const fieldDef = getFieldDef(meta, fieldName);
  return row[fieldDef?.column ?? fieldName];
}

// ─── 3-Line Row Helpers ────────────────────────────

interface ThreeLineLineProps {
  elements: ThreeLineElement[];
  row: DataRow;
  meta: EntityMeta;
  refResolver?: RefResolverHandle;
  /** primary = bold/normal, secondary = normal, tertiary = muted smaller */
  lineStyle: 'primary' | 'secondary' | 'tertiary';
}

function ThreeLineLine({ elements, row, meta, refResolver, lineStyle }: ThreeLineLineProps) {
  if (!elements || elements.length === 0) return null;

  const lineClasses =
    lineStyle === 'primary'
      ? 'text-sm font-medium text-foreground'
      : lineStyle === 'secondary'
        ? 'text-sm text-foreground'
        : 'text-xs text-muted-foreground';

  return (
    <div className={`flex items-center gap-2 ${lineClasses} truncate`}>
      {elements.map((el, i) => {
        const fieldDef = getFieldDef(meta, el.field);
        let rawVal = getRowValue(row, el.field, meta);

        // Resolve FK display
        if (refResolver && fieldDef && refResolver.isFkField(el.field)) {
          const display = refResolver.getDisplay(el.field, String(rawVal ?? ''));
          if (display) rawVal = display;
        }

        const formatted = fieldDef
          ? formatCell(rawVal, el.format ?? fieldDef.type)
          : summarizeValue(rawVal);

        if (el.badge) {
          return (
            <Badge key={i} variant="outline" className="text-[11px] px-1.5 py-0 h-5 shrink-0">
              {formatted}
            </Badge>
          );
        }

        return (
          <span
            key={i}
            className={`truncate ${el.muted ? 'text-muted-foreground' : ''}`}
          >
            {formatted}
          </span>
        );
      })}
    </div>
  );
}

// ─── Column Group Helpers ──────────────────────────

/**
 * Auto-distribute a group's fields into up to 3 lines when no explicit layout is given.
 *   ≤3 fields → one per line
 *   4-6 → first 2 on line1, next 2 on line2, rest on line3
 *   >6  → first 3 / next 3 / rest (truncated to 3)
 * First badge/enum/status field gets `badge: true`.
 */
function autoThreeLineLayout(
  fields: string[],
  meta: EntityMeta,
): { line1: ThreeLineElement[]; line2?: ThreeLineElement[]; line3?: ThreeLineElement[] } {
  if (fields.length === 0) return { line1: [] };

  const toEl = (f: string): ThreeLineElement => {
    const def = getFieldDef(meta, f);
    const isBadge = def?.type === 'enum' || f === 'status' || f.endsWith('_status');
    return { field: f, badge: isBadge || undefined, muted: undefined };
  };

  if (fields.length <= 3) {
    return {
      line1: [toEl(fields[0]!)],
      line2: fields[1] ? [toEl(fields[1])] : undefined,
      line3: fields[2] ? [toEl(fields[2])] : undefined,
    };
  }
  if (fields.length <= 6) {
    return {
      line1: fields.slice(0, 2).map(toEl),
      line2: fields.slice(2, 4).map(toEl),
      line3: fields.length > 4 ? fields.slice(4, 6).map(toEl) : undefined,
    };
  }
  return {
    line1: fields.slice(0, 3).map(toEl),
    line2: fields.slice(3, 6).map(toEl),
    line3: fields.slice(6, 9).map(toEl),
  };
}

/**
 * Renders a single group block inside a 3-line grouped card row.
 * Shows the group label as a small header, then up to 3 lines of field content.
 */
function ThreeLineGroupBlock({
  group,
  row,
  meta,
  refResolver,
}: {
  group: ColumnGroupDSL;
  row: DataRow;
  meta: EntityMeta;
  refResolver?: RefResolverHandle;
}) {
  const layout = group.threeLineLayout ?? autoThreeLineLayout(group.columns, meta);

  return (
    <div className="flex-1 min-w-0 space-y-0.5">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70 mb-0.5">
        {group.label}
      </div>
      <ThreeLineLine elements={layout.line1} row={row} meta={meta} refResolver={refResolver} lineStyle="primary" />
      {layout.line2 && layout.line2.length > 0 && (
        <ThreeLineLine elements={layout.line2} row={row} meta={meta} refResolver={refResolver} lineStyle="secondary" />
      )}
      {layout.line3 && layout.line3.length > 0 && (
        <ThreeLineLine elements={layout.line3} row={row} meta={meta} refResolver={refResolver} lineStyle="tertiary" />
      )}
    </div>
  );
}

// ─── Main Component ────────────────────────────────

export function UniversalGrid({
  slug,
  meta,
  columns: dslColumns,
  data,
  total,
  page,
  pageSize,
  onPageChange,
  onSort,
  onCellEdit,
  onRowClick,
  onSelectionChange,
  userRole,
  loading,
  sortField,
  sortOrder = 'desc',
  refResolver,
  density = 'default',
  onQuickEdit,
  onQuickCopyRow,
  rowClassName,
  prependColumns,
  rowMode = '1-line',
  threeLineConfig,
  columnGroups,
}: UniversalGridProps) {
  const is3Line = rowMode === '3-line';
  const isBasic = rowMode === 'basic';
  const isJson = rowMode === 'json';
  const isCardMode = is3Line || isJson;
  const densityClasses = DENSITY_CLASSES[density];
  const rowHeight = isCardMode ? 72 : isBasic ? 32 : density === 'compact' ? 28 : density === 'comfortable' ? 48 : 40;
  const { t } = useTranslation();
  const tableContainerRef = useRef<HTMLDivElement>(null);

  // ─── Local State ─────────────────────────────────
  const [editingCell, setEditingCell] = useState<{ rowId: string; field: string } | null>(null);
  const [sorting, setSorting] = useState<SortingState>(
    sortField ? [{ id: sortField, desc: sortOrder === 'desc' }] : [],
  );
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({});
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  // ─── Visible columns ────────────────────────────
  const visibleDslColumns = useMemo(
    () => dslColumns.filter((c) => !c.hidden),
    [dslColumns],
  );

  // ─── TanStack Column Definitions ─────────────────
  /** Build one leaf column def for a DSL column */
  const buildDataColumn = useCallback(
    (dslCol: ListColumnDSL, columnId = dslCol.field): ColumnDef<DataRow> => {
      const fieldDef = getFieldDef(meta, dslCol.field);
      const editability = getCellEditability(
        dslCol.editable,
        dslCol.editableBy,
        fieldDef?.updatable,
        userRole,
      );

      return {
        id: columnId,
        accessorFn: (row) => getRowValue(row, dslCol.field, meta),
        header: () => {
          const isSorted = sortField === dslCol.field;
          return (
            <div
              className={`inline-flex items-center gap-1 select-none ${
                dslCol.sortable !== false ? 'cursor-pointer' : ''
              }`}
              onClick={() => {
                if (dslCol.sortable === false) return;
                const newOrder = isSorted && sortOrder === 'asc' ? 'desc' : 'asc';
                onSort(dslCol.field, newOrder);
              }}
            >
              <span className="font-medium text-xs uppercase tracking-wider">
                {dslCol.label ?? dslCol.field}
              </span>
              {dslCol.sortable !== false && (
                isSorted
                  ? (sortOrder === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)
                  : <ArrowUpDown className="h-3 w-3 opacity-30" />
              )}
              {editability === 'locked' && <span className="ml-0.5 text-xs">🔒</span>}
            </div>
          );
        },
        cell: ({ row }) => {
          const rowId = getRowId(row.original);
          const cellValue = getRowValue(row.original, dslCol.field, meta);
          const isEditing = editingCell?.rowId === rowId && editingCell?.field === dslCol.field;

          // Editing mode
          if (isEditing && fieldDef && editability === 'editable') {
            return (
              <GridCellEditor
                value={cellValue}
                fieldDef={fieldDef}
                onCommit={async (newValue) => {
                  setEditingCell(null);
                  if (newValue !== cellValue && onCellEdit) {
                    await onCellEdit(rowId, dslCol.field, newValue);
                  }
                }}
                onCancel={() => setEditingCell(null)}
              />
            );
          }

          // Display mode — resolve FK fields to human-readable name
          let formatted: ReactNode;
          const isFK = refResolver?.isFkField(dslCol.field);
          if (isFK && typeof cellValue === 'string' && cellValue) {
            formatted = refResolver!.getDisplay(dslCol.field, cellValue) ?? formatCell(cellValue, dslCol.format ?? fieldDef?.type);
          } else {
            formatted = formatCell(cellValue, dslCol.format ?? fieldDef?.type);
          }

          // Status badge
          if (dslCol.format === 'status' && cellValue) {
            return (
              <Badge variant="outline" className="text-xs font-normal">
                {String(cellValue)}
              </Badge>
            );
          }

          return (
            <div className="flex items-center gap-1.5">
              <span className="truncate">{formatted}</span>
              {editability === 'locked' && <LockIndicator />}
            </div>
          );
        },
        size: dslCol.width ?? dslCol.minWidth ?? 150,
        minSize: dslCol.minWidth ?? 80,
        enableSorting: dslCol.sortable !== false,
        enableResizing: true,
        meta: { dslCol, editability, fieldDef },
      };
    },
    [meta, userRole, editingCell, sortField, sortOrder, onCellEdit, onSort, refResolver],
  );

  const tanstackColumns = useMemo<ColumnDef<DataRow>[]>(() => {
    const cols: ColumnDef<DataRow>[] = [];

    // Selection checkbox column
    if (onSelectionChange) {
      cols.push({
        id: '_select',
        size: 40,
        minSize: 40,
        maxSize: 40,
        enableResizing: false,
        header: ({ table }) => (
          <input
            type="checkbox"
            name="select-all-rows"
            aria-label="Select all rows"
            checked={table.getIsAllPageRowsSelected()}
            onChange={table.getToggleAllPageRowsSelectedHandler()}
            className="h-4 w-4 rounded border-gray-300"
          />
        ),
        cell: ({ row }) => (
          <input
            type="checkbox"
            name={`select-row-${row.id}`}
            aria-label={`Select row ${row.id}`}
            checked={row.getIsSelected()}
            onChange={row.getToggleSelectedHandler()}
            className="h-4 w-4 rounded border-gray-300"
            onClick={(e) => e.stopPropagation()}
          />
        ),
      });
    }

    // Caller-injected columns (e.g. suspense badge)
    if (prependColumns?.length) {
      cols.push(...prependColumns);
    }

    // ─── Column grouping vs flat ───
    if (columnGroups && columnGroups.length > 0) {
      // Collect grouped field names for determining ungrouped columns
      const groupedFieldSet = new Set(columnGroups.flatMap((g) => g.columns));

      for (const group of columnGroups) {
        const childCols = group.columns
          .map((f) => visibleDslColumns.find((c) => c.field === f))
          .filter((c): c is ListColumnDSL => !!c)
          .map((dslCol, index) => buildDataColumn(dslCol, `${group.key}_${dslCol.field}_${index}`));

        if (childCols.length > 0) {
          cols.push({
            id: `_group_${group.key}`,
            header: () => (
              <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                {group.label}
              </span>
            ),
            columns: childCols,
          });
        }
      }

      // Ungrouped columns go into a catch-all (no group header)
      const ungrouped = visibleDslColumns.filter((c) => !groupedFieldSet.has(c.field));
      if (ungrouped.length > 0) {
        cols.push(...ungrouped.map((dslCol, index) => buildDataColumn(dslCol, `ungrouped_${dslCol.field}_${index}`)));
      }
    } else {
      // Flat — no groups
      for (const [index, dslCol] of visibleDslColumns.entries()) {
        cols.push(buildDataColumn(dslCol, `${dslCol.field}_${index}`));
      }
    }

    if (onQuickEdit || onQuickCopyRow) {
      cols.push({
        id: '_actions',
        size: 92,
        minSize: 92,
        maxSize: 92,
        enableSorting: false,
        enableResizing: false,
        header: () => (
          <span className="font-medium text-xs uppercase tracking-wider text-muted-foreground">
            操作
          </span>
        ),
        cell: ({ row }) => (
          <div className="flex items-center justify-end gap-1">
            {onQuickEdit && (
              <button
                type="button"
                className="inline-flex h-7 w-7 items-center justify-center rounded border border-border bg-background text-muted-foreground hover:bg-primary/5 hover:text-primary"
                title="快速编辑"
                onClick={(e) => {
                  e.stopPropagation();
                  onQuickEdit(row.original);
                }}
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
            )}
            {onQuickCopyRow && (
              <button
                type="button"
                className="inline-flex h-7 w-7 items-center justify-center rounded border border-border bg-background text-muted-foreground hover:bg-primary/5 hover:text-primary"
                title="复制本行"
                onClick={(e) => {
                  e.stopPropagation();
                  onQuickCopyRow(row.original);
                }}
              >
                <Copy className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        ),
      });
    }

    return cols;
  }, [visibleDslColumns, buildDataColumn, onSelectionChange, onQuickEdit, onQuickCopyRow, prependColumns, columnGroups]);

  // ─── TanStack Table Instance ─────────────────────
  const table = useReactTable({
    data,
    columns: tanstackColumns,
    state: { sorting, columnSizing, rowSelection },
    onSortingChange: setSorting,
    onColumnSizingChange: setColumnSizing,
    onRowSelectionChange: (updater) => {
      const next = typeof updater === 'function' ? updater(rowSelection) : updater;
      setRowSelection(next);
      if (onSelectionChange) {
        const selectedIds = Object.keys(next)
          .filter((k) => next[k])
          .map((idx) => {
            const row = data[Number(idx)];
            return row ? getRowId(row) : '';
          })
          .filter(Boolean);
        onSelectionChange(selectedIds);
      }
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: (row) => getRowId(row),
    columnResizeMode: 'onChange',
    enableRowSelection: !!onSelectionChange,
    manualSorting: true,
  });

  // ─── Virtual Rows ───────────────────────────────
  const { rows: tableRows } = table.getRowModel();
  const virtualizer = useVirtualizer({
    count: tableRows.length,
    getScrollElement: () => tableContainerRef.current,
    estimateSize: () => rowHeight,
    overscan: 10,
  });

  // ─── Double-click handler ────────────────────────
  const handleDoubleClick = useCallback(
    (rowId: string, field: string) => {
      const dslCol = visibleDslColumns.find((c) => c.field === field);
      const fieldDef = getFieldDef(meta, field);
      const editability = getCellEditability(
        dslCol?.editable,
        dslCol?.editableBy,
        fieldDef?.updatable,
        userRole,
      );
      if (editability === 'editable' && onCellEdit) {
        setEditingCell({ rowId, field });
      }
    },
    [visibleDslColumns, meta, userRole, onCellEdit],
  );

  // ─── Pagination ──────────────────────────────────
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // ─── Selected count ──────────────────────────────
  const selectedCount = Object.values(rowSelection).filter(Boolean).length;

  const handleCopySelected = useCallback(async () => {
    const selectedRows = data.filter((row) => Boolean(rowSelection[getRowId(row)]));
    if (selectedRows.length === 0) return;

    const lines = [
      visibleDslColumns.map((column) => column.label ?? column.field).join('\t'),
      ...selectedRows.map((row) =>
        visibleDslColumns.map((column) => {
          const value = getRowValue(row, column.field, meta);
          return summarizeValue(value).replace(/\t/g, ' ').replace(/\r?\n/g, ' ');
        }).join('\t'),
      ),
    ];
    const payload = lines.join('\n');

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(payload);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = payload;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
    } catch {
      // ignore clipboard failure here; user still has export + other actions
    }
  }, [data, meta, rowSelection, visibleDslColumns]);

  // ─── Render ──────────────────────────────────────
  return (
    <div className="space-y-2">
      {/* Selection toolbar */}
      {selectedCount > 0 && (
        <div className="flex items-center gap-3 rounded-md bg-primary/5 border border-primary/20 px-3 py-2 text-sm">
          <Check className="h-4 w-4 text-primary" />
          <span className="font-medium">{selectedCount} {t('grid.selected', 'selected')}</span>
          <button
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground underline"
            onClick={() => void handleCopySelected()}
          >
            <Copy className="h-3.5 w-3.5" />
            {t('grid.copySelected', 'Copy')}
          </button>
          <button
            className="ml-auto text-xs text-muted-foreground hover:text-foreground underline"
            onClick={() => setRowSelection({})}
          >
            {t('grid.clearSelection', 'Clear selection')}
          </button>
        </div>
      )}

      {/* Table container with virtual scroll */}
      <div
        ref={tableContainerRef}
        className="rounded-md border overflow-auto"
        style={{ maxHeight: '70vh' }}
      >
        {/* ─── Basic view mode (compact table with header row) ─── */}
        {isBasic ? (
          <table className="w-full text-sm">
            <thead className="sticky top-0 z-10 bg-muted/60 backdrop-blur-sm">
              <tr>
                {visibleDslColumns.map((col, index) => (
                  <th
                    key={`${col.field}_${index}`}
                    className="px-3 py-2 text-left text-[10px] font-medium uppercase tracking-wider text-muted-foreground whitespace-nowrap"
                  >
                    {col.label ?? col.field}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading ? (
                <tr>
                  <td colSpan={visibleDslColumns.length} className="h-32 text-center">
                    <div className="inline-flex items-center gap-2">
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-muted border-t-primary" />
                      <span className="text-sm text-muted-foreground">{t('grid.loading', 'Loading...')}</span>
                    </div>
                  </td>
                </tr>
              ) : data.length === 0 ? (
                <tr>
                  <td colSpan={visibleDslColumns.length} className="h-32 text-center text-sm text-muted-foreground">
                    {t('grid.noData', 'No data')}
                  </td>
                </tr>
              ) : (
                data.map((row, idx) => {
                  const rowId = getRowId(row);
                  const customRowClassName = rowClassName?.(row) ?? '';
                  return (
                    <tr
                      key={rowId || idx}
                      data-row-id={rowId || undefined}
                      className={`hover:bg-muted/50 cursor-pointer ${customRowClassName}`}
                      onClick={() => onRowClick?.(row)}
                    >
                      {visibleDslColumns.map((col, index) => {
                        const rawValue = getRowValue(row, col.field, meta);
                        const fieldDef = getFieldDef(meta, col.field);
                        const isFK = refResolver?.isFkField(col.field);
                        const cellValue = typeof rawValue === 'string' ? rawValue : String(rawValue ?? '');
                        const display = isFK && cellValue
                          ? (refResolver!.getDisplay(col.field, cellValue) ?? formatCell(rawValue, col.format ?? fieldDef?.type))
                          : formatCell(rawValue, col.format ?? fieldDef?.type);
                        return (
                          <td key={`${col.field}_${index}`} className="px-3 py-1.5 truncate max-w-[200px]">
                            {display}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        ) : isJson ? (
        /* ─── JSON view mode (raw JSON per row) ─── */
          <div className="divide-y">
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-muted border-t-primary" />
                <span className="ml-2 text-sm text-muted-foreground">{t('grid.loading', 'Loading...')}</span>
              </div>
            ) : data.length === 0 ? (
              <div className="flex items-center justify-center h-32 text-sm text-muted-foreground">
                {t('grid.noData', 'No data')}
              </div>
            ) : (
              data.map((row, idx) => {
                const rowId = getRowId(row);
                return (
                  <details
                    key={rowId || idx}
                    className="group border-b last:border-b-0"
                  >
                    <summary className="px-4 py-2 cursor-pointer hover:bg-muted/50 text-sm font-mono flex items-center gap-2">
                      <span className="text-muted-foreground">#{idx + 1}</span>
                      <span className="truncate">{rowId || `row-${idx}`}</span>
                      <span className="ml-auto text-xs text-muted-foreground">
                        {Object.keys(row).length} fields
                      </span>
                    </summary>
                    <pre className="px-4 py-2 text-xs font-mono bg-muted/30 overflow-x-auto whitespace-pre-wrap break-all max-h-[400px] overflow-y-auto">
                      {JSON.stringify(row, null, 2)}
                    </pre>
                  </details>
                );
              })
            )}
          </div>
        ) : is3Line && (threeLineConfig || columnGroups) ? (
          <div className="divide-y">
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-muted border-t-primary" />
                <span className="ml-2 text-sm text-muted-foreground">{t('grid.loading', 'Loading...')}</span>
              </div>
            ) : data.length === 0 ? (
              <div className="flex items-center justify-center h-32 text-sm text-muted-foreground">
                {t('grid.noData', 'No data')}
              </div>
            ) : (
              data.map((row, idx) => {
                const rowId = getRowId(row);
                const isSelected = Boolean(rowSelection[rowId]);
                const customRowClassName = rowClassName?.(row) ?? '';

                return (
                  <div
                    key={rowId || idx}
                    data-row-id={rowId || undefined}
                    className={`flex items-start gap-3 px-3 py-2.5 transition-colors hover:bg-muted/50 cursor-pointer ${
                      isSelected ? 'bg-primary/5' : ''
                    } ${customRowClassName}`}
                    onClick={() => onRowClick?.(row)}
                  >
                    {/* Selection checkbox */}
                    {onSelectionChange && (
                      <input
                        type="checkbox"
                        name={`select-row-${rowId}`}
                        aria-label={`Select row ${rowId}`}
                        checked={isSelected}
                        onChange={() => {
                          const next = { ...rowSelection, [rowId]: !isSelected };
                          if (!next[rowId]) delete next[rowId];
                          setRowSelection(next);
                          const ids = Object.keys(next).filter((k) => next[k]);
                          onSelectionChange(ids);
                        }}
                        className="h-4 w-4 rounded border-gray-300 mt-0.5 shrink-0"
                        onClick={(e) => e.stopPropagation()}
                      />
                    )}

                    {/* Content: grouped blocks or flat 3-line */}
                    {columnGroups && columnGroups.length > 0 ? (
                      <div className="flex-1 min-w-0 flex gap-4">
                        {columnGroups.map((group) => (
                          <ThreeLineGroupBlock
                            key={group.key}
                            group={group}
                            row={row}
                            meta={meta}
                            refResolver={refResolver}
                          />
                        ))}
                      </div>
                    ) : threeLineConfig ? (
                      <div className="flex-1 min-w-0 space-y-0.5">
                        <ThreeLineLine elements={threeLineConfig.line1} row={row} meta={meta} refResolver={refResolver} lineStyle="primary" />
                        <ThreeLineLine elements={threeLineConfig.line2} row={row} meta={meta} refResolver={refResolver} lineStyle="secondary" />
                        <ThreeLineLine elements={threeLineConfig.line3} row={row} meta={meta} refResolver={refResolver} lineStyle="tertiary" />
                      </div>
                    ) : null}

                    {/* Action buttons */}
                    {(onQuickEdit || onQuickCopyRow) && (
                      <div className="flex items-center gap-1 shrink-0 mt-0.5">
                        {onQuickEdit && (
                          <button
                            type="button"
                            className="inline-flex h-7 w-7 items-center justify-center rounded border border-border bg-background text-muted-foreground hover:bg-primary/5 hover:text-primary"
                            title="快速编辑"
                            onClick={(e) => { e.stopPropagation(); onQuickEdit(row); }}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                        )}
                        {onQuickCopyRow && (
                          <button
                            type="button"
                            className="inline-flex h-7 w-7 items-center justify-center rounded border border-border bg-background text-muted-foreground hover:bg-primary/5 hover:text-primary"
                            title="复制"
                            onClick={(e) => { e.stopPropagation(); onQuickCopyRow(row); }}
                          >
                            <Copy className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        ) : (
        /* ─── Normal 1-line table mode ─── */
        <table className="w-full border-collapse text-sm">
          {/* Header */}
          <thead className="sticky top-0 z-10 bg-muted/80 backdrop-blur-sm">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className={`relative ${densityClasses.cell} text-left font-medium text-muted-foreground border-b ${densityClasses.text}`}
                    style={{ width: header.getSize() }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}

                    {/* Column resize handle */}
                    {header.column.getCanResize() && (
                      <div
                        onMouseDown={header.getResizeHandler()}
                        onTouchStart={header.getResizeHandler()}
                        className={`absolute right-0 top-0 h-full w-1 cursor-col-resize select-none touch-none hover:bg-primary/40 ${
                          header.column.getIsResizing() ? 'bg-primary/60' : ''
                        }`}
                      />
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          {/* Body */}
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={tanstackColumns.length} className="h-32 text-center text-muted-foreground">
                  <div className="flex items-center justify-center gap-2">
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-muted border-t-primary" />
                    {t('grid.loading', 'Loading...')}
                  </div>
                </td>
              </tr>
            ) : tableRows.length === 0 ? (
              <tr>
                <td colSpan={tanstackColumns.length} className="h-32 text-center text-muted-foreground">
                  {t('grid.noData', 'No data')}
                </td>
              </tr>
            ) : (
              <>
                {virtualizer.getVirtualItems().length > 0 && (
                  <tr style={{ height: virtualizer.getVirtualItems()[0]?.start ?? 0 }}>
                    <td />
                  </tr>
                )}
                {virtualizer.getVirtualItems().map((virtualRow) => {
                  const row = tableRows[virtualRow.index];
                  if (!row) return null;
                  const rowId = getRowId(row.original);
                  const customRowClassName = rowClassName?.(row.original) ?? '';

                  return (
                    <tr
                      key={row.id}
                      data-index={virtualRow.index}
                      data-row-id={rowId || undefined}
                      className={`border-b transition-colors hover:bg-muted/50 ${
                        row.getIsSelected() ? 'bg-primary/5' : ''
                      } ${customRowClassName}`}
                      onClick={() => onRowClick?.(row.original)}
                      style={{ height: virtualRow.size }}
                    >
                      {row.getVisibleCells().map((cell) => {
                        const colMeta = cell.column.columnDef.meta as
                          | { dslCol?: ListColumnDSL; editability: string }
                          | undefined;
                        const isReadonly = colMeta?.editability === 'readonly';

                        return (
                          <td
                            key={cell.id}
                            className={`${densityClasses.cell} ${densityClasses.text} ${
                              isReadonly ? '' : 'cursor-default'
                            } ${
                              colMeta?.editability === 'editable'
                                ? 'hover:bg-blue-50/50 dark:hover:bg-blue-950/20'
                                : ''
                            }`}
                            style={{ width: cell.column.getSize() }}
                            onDoubleClick={() => {
                              if (cell.column.id !== '_select') {
                                handleDoubleClick(rowId, colMeta?.dslCol?.field ?? cell.column.id);
                              }
                            }}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
                {virtualizer.getVirtualItems().length > 0 && (
                  <tr
                    style={{
                      height:
                        virtualizer.getTotalSize() -
                        (virtualizer.getVirtualItems().at(-1)?.end ?? 0),
                    }}
                  >
                    <td />
                  </tr>
                )}
              </>
            )}
          </tbody>
        </table>
        )}
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground">
          {t('grid.pageInfo', {
            defaultValue: 'Page {{current}} of {{total}} ({{count}} records)',
            current: page,
            total: totalPages,
            count: total,
          })}
        </span>
        <div className="flex gap-1">
          <button
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
            className="rounded-md border px-3 py-1.5 text-xs hover:bg-muted disabled:opacity-40"
          >
            ← {t('grid.prev', 'Prev')}
          </button>
          <button
            disabled={page >= totalPages}
            onClick={() => onPageChange(page + 1)}
            className="rounded-md border px-3 py-1.5 text-xs hover:bg-muted disabled:opacity-40"
          >
            {t('grid.next', 'Next')} →
          </button>
        </div>
      </div>
    </div>
  );
}

export default UniversalGrid;
